import React, { useContext, useEffect, useState } from 'react';
import {
	IVignetteOffer,
	OfferContext,
	getPriceAmountByCatalogId,
	VignetteContext,
	calculateNewDates,
	convertApiDate,
	IDates,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
} from '@adac-mp/shared';
import { Checkbox, ProductOptionLabel, RadioButtons, TollRoute, Typography, VignetteAUT } from '../../index';

interface IMultiSelectionProps {
	hasError?: boolean;
	index: number;
	products: IVignetteOffer[];
	defaultVignetteId?: string;
	defaultModuleValue?: boolean;
	supplierId?: number;
	configure?: boolean;
	dates?: IDates | undefined;
	onVignetteChanged: (vignette: IVignetteOffer, multiSelect?: boolean) => void;
	moduleAndProductIsSelectedById: (index: number, checked: boolean, isSelected: boolean) => void;
}
export const MultiSelection = ({
	index,
	hasError,
	products,
	defaultVignetteId = undefined,
	defaultModuleValue = false,
	supplierId = undefined,
	configure = false,
	dates,
	onVignetteChanged,
	moduleAndProductIsSelectedById,
}: IMultiSelectionProps) => {
	const { vignetteOffers, labels } = useContext(OfferContext);
	const { selectedProducts, contentfulLabels, removeSelectedProductsBySupplierAndCategory } =
		useContext(VignetteContext);
	const [moduleSelected, setModuleSelected] = useState<boolean>(defaultModuleValue);
	const [vignetteId, setVignetteId] = useState<string | undefined>(defaultVignetteId);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const hasMoreThanOneProduct = products.length > 1;

	const renderProductIcon = (moduleTypeId: number) => {
		if (supplierId !== 3) return;
		return moduleTypeId === 2 ? <VignetteAUT iconAsBadge /> : <TollRoute iconAsBadge />;
	};

	const getCheapestProductPrice = () => {
		const cheapestproduct =
			products.length > 0
				? products
						.filter(({ moduleTypeId }) => moduleTypeId !== 4)
						.reduce((acc, curr) => {
							const [accTotal] = acc.prices;
							const [currTotal] = curr.prices;
							return accTotal.amount.decimalAmount < currTotal.amount.decimalAmount ? acc : curr;
						})
				: undefined;
		const generalPriceComponents = cheapestproduct?.priceComponents ?? [];
		const generalPrice = getPriceAmountByCatalogId(generalPriceComponents);
		return generalPrice;
	};

	const renderCheckboxLabel = () => {
		return (
			<span className="flex justify-between gap-2 items-center md:items-start">
				<span>
					<Typography variant="title" size="lg" component="p" color="title">
						{products[0].moduleTypeId === 2
							? products[0].moduleType
							: getLabelValue('mp.tollSection', labels)}
					</Typography>
				</span>
				<span className="flex gap-2">
					<span className="flex flex-col">
						<Typography
							className="whitespace-nowrap"
							color="title"
							variant="title"
							size="lg"
							component="p">{`${
							getLabelValue('mp.from', labels) || 'ab'
						} ${getCheapestProductPrice()}€`}</Typography>
					</span>
					{renderProductIcon(products[0].moduleTypeId)}
				</span>
			</span>
		);
	};

	const selectOption = (option: string) => {
		const selectedVignetteId = option;
		const currentDates = selectedProducts[0]?.dates || dates;
		const currentOffers =
			products[0].moduleTypeId === 3 || products[0].moduleTypeId === 4 ? products : vignetteOffers;
		const currentVignette = currentOffers.find(
			({ serviceCatalogCode, usageFrom, usageTo }) =>
				serviceCatalogCode === selectedVignetteId &&
				(!currentDates ||
					(currentDates &&
						currentDates.start.getTime() >= convertApiDate(usageFrom).getTime() &&
						currentDates.start.getTime() <= convertApiDate(usageTo).getTime())),
		);
		setVignetteId(selectedVignetteId);
		if (currentVignette) {
			if (selectedProducts[0]?.dates) {
				const prevStartDate = selectedProducts[0]?.dates?.start;
				const newDates = calculateNewDates(prevStartDate, currentVignette);
				onVignetteChanged(
					{
						...currentVignette,
						dates: newDates,
						plates: selectedProducts[0]?.plates,
					},
					true,
				);
			} else if (currentDates) {
				const newDates = calculateNewDates(currentDates?.start, currentVignette);
				onVignetteChanged({ ...currentVignette, dates: newDates }, true);
			} else {
				onVignetteChanged(currentVignette, true);
			}
			moduleAndProductIsSelectedById(index, true, true);
		}
	};

	useEffect(() => {
		if (defaultVignetteId) {
			selectOption(defaultVignetteId);
		}
	}, []);

	const handleCheckboxClick = (isChecked: boolean) => {
		setModuleSelected(isChecked);
		if (!isChecked) {
			removeSelectedProductsBySupplierAndCategory(products[0].supplierId, products[0].moduleTypeId);
			setVignetteId(undefined);
			moduleAndProductIsSelectedById(index, false, false);
		} else if (!hasMoreThanOneProduct) {
			selectOption(products[0].serviceCatalogCode);
		} else {
			moduleAndProductIsSelectedById(index, true, false);
		}
	};

	return (
		<>
			<Checkbox
				className="flex group focus-visible:outline-primary cursor-pointer bg-white px-4 py-2 rounded mb-1.5 last:mb-0 md:py-3"
				labelClassName="w-full"
				data-cy={`Section: ${products[0].moduleType}`}
				title={products[0].moduleType}
				label={renderCheckboxLabel()}
				checked={moduleSelected}
				setChecked={handleCheckboxClick}
			/>
			{moduleSelected && hasMoreThanOneProduct ? (
				<RadioButtons
					className={`${
						configure && 'px-4'
					} py-2 border-b border-l-0 border-t-0 border-r-0 border-solid border-b-gray-600 last:border-b-0 last:rounded-b md:py-3`}
					labelClassName="w-full"
					value={String(vignetteId)}
					hasError={!vignetteId && moduleSelected && hasError}
					helperText={currentLabels?.errorProductSelection || 'Bitte wählen Sie einen Artikel aus'}
					helperTextStyle={{ marginTop: '10px', gap: '12px', paddingInline: '28px', paddingBottom: '8px' }}
					wrapperClassName="bg-white rounded"
					setValue={(option) => {
						selectOption(option);
					}}
					size="md"
					options={products.map(
						({ serviceCatalogCode, serviceCatalogName, priceComponents, moduleTypeId, downtimes }) => ({
							id: serviceCatalogCode,
							label: (
								<ProductOptionLabel
									supplierId={supplierId}
									catalogId={serviceCatalogCode}
									serviceCatalogName={
										moduleTypeId === 3
											? getLabelValue('mp.singleTicket', labels) || 'Einzelfahrkarte'
											: serviceCatalogName
									}
									priceComponents={priceComponents}
									moduleTypeId={moduleTypeId}
									downtimes={downtimes}
								/>
							),
							title: serviceCatalogName,
						}),
					)}
				/>
			) : null}
		</>
	);
};
